import { create } from 'zustand';
import MuvikaService from '../services/muvika';
import { generateId } from '../services/random';
import accountStore from './account';
import ExternalViewCommands from '../services/external';
import messageStore from './message';
import musicPlayerStore from './musicPlayer';
import { decades } from './../common/decades';
import TR from './../Trans';
import settingsStore from './settings';
import { subscribeWithSelector } from 'zustand/middleware';

const itemsOnPage = 25;
const maxPlayQueueSize = 2200;

const initialState = {
  isWaiting: false,
  totalCount: 0,
  history: null,
  currentPlayList: null,
  playQueue: null,
  currentPosition: 0,
  currentPerformance: null,
  nextPerformance: null,
  playQueueDuration: 0,
  playLists: null,
  latestReqId: -1,
  performanceToBeChanged: null,
  repeatPlayList: null
};

const bgPerformanceStore = create(subscribeWithSelector((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setRepeatPlayList: (playList) => {
    set({ repeatPlayList: playList });
  },
  setRepeatPlayListFromId: (playListId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id === playListId);
      if (playListIndex != -1) {
        get().setRepeatPlayList(playLists[playListIndex]);
      }
    }
  },
  setNextPerformance: (nextPerformance) => {
    set({ nextPerformance: nextPerformance });
  },
  setCurrentPerformance: (performance) => {
    set({ currentPerformance: performance });
  },
  setCurrentTitle: (title) => {
    set({ currentTitle: title });
  },
  setPlayQueueTrackList: (list) => {
    set({ playQueueTrackList: list });
  },
  copyPlayList: (sessionToken, playList, playListName) => {
    MuvikaService.saveMusicPlayList(sessionToken, 0, playListName, playList.items)
      .then(function (res) {
        if (res.data.error) {
          let playLists = get().playLists;
          if (playLists) {
            let playListIndex = playLists.findIndex(x => x.name === playListName);
            if (playListIndex != -1) {
              messageStore.getState().setError(sessionToken, res.data.error.code, TR.cm_same_playlist_exists_already);
            } else {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            }
          }
        } else {
          messageStore.getState().setMessage(sessionToken, TR.cm_playlist_saved);
          get().getPlayLists(sessionToken);
        }
      })
      .catch(function () { });
  },
  savePlayList: (sessionToken, playListId, playListName) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        MuvikaService.saveMusicPlayList(sessionToken, playListId, playListName, playQueue.items)
          .then(function (res) {
            if (res.data.error) {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            } else {
              messageStore.getState().setMessage(sessionToken, TR.cm_playlist_saved);
              get().getPlayLists(sessionToken);
            }
          })
          .catch(function () { });
      }
    }
  },
  changePlayListName: (sessionToken, playListName, playList) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.name === playListName);
      if (playListIndex != -1) {
        messageStore.getState().setError(sessionToken, 0, TR.cm_same_playlist_exists_already);
      } else {
        MuvikaService.editMusicPlayList(sessionToken, playListName, playList)
          .then(function (res) {
            if (res.data.error) {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            } else {
              let playLists = get().playLists;
              if (playLists) {
                let playListIndex = playLists.findIndex(x => x.id == playList.id);
                if (playListIndex != -1) {
                  playLists[playListIndex].name = playListName;
                  set({ playLists: [...playLists] });
                  messageStore.getState().setMessage(sessionToken, TR.cm_playlist_saved);
                }
              }
            }
          })
          .catch(function () { });
      }
    }
  },
  getPlayLists: (sessionToken, isWaiting = true) => {
    set({ isWaiting: isWaiting });
    MuvikaService.getMusicPlayLists(sessionToken)
      .then(function (res) {
        //console.log(res);
        if (res.data.error) {
          set({ isSet: false, playLists: [], isWaiting: false });
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let playLists = [...res.data.data.items];
          for (let i = 0; i < playLists.length; i++) {
            let duration = 0;
            for (let j = 0; j < playLists[i].items.length; j++) {
              playLists[i].items[j].position = j;
              playLists[i].items[j].incoming = duration;
              duration += playLists[i].items[j].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
          }
          set({ isSet: true, playLists: playLists, totalCount: res.data.data.items.length, isWaiting: false });
        }
      })
      .catch(function () { set({ isWaiting: false }); });
  },
  createPlayList: (sessionToken, playListName) => {
    MuvikaService.createMusicPlayList(sessionToken, playListName)
      .then(function (res) {
        //console.log(res);
        if (res.data.error) {
          let playLists = get().playLists;
          if (playLists) {
            let playListIndex = playLists.findIndex(x => x.name === playListName);
            if (playListIndex != -1) {
              messageStore.getState().setError(sessionToken, res.data.error.code, TR.cm_same_playlist_exists_already);
            } else {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            }
          } else {
            messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
          }
        } else {
          get().getPlayLists(sessionToken);
        }
      });
  },
  deletePlayList: (sessionToken, playListId) => {
    MuvikaService.deleteMusicPlayList(sessionToken, playListId)
      .then(function (res) {
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let playLists = get().playLists;
          if (playLists) {
            let playListIndex = playLists.findIndex(x => x.id == playListId);
            if (playListIndex != -1) {
              playLists.splice(playListIndex, 1);
              set({ playLists: [...playLists] });
              messageStore.getState().setMessage(sessionToken, TR.cm_deleted_big);
            }
          }
        }
      })
      .catch(function () { });
  },
  getPlayQueue: (sessionToken) => {
    get().clearPlayQueue(sessionToken);
    MuvikaService.getMusicPlayQueue(sessionToken)
      .then(function (res) {
        console.log(res);
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let playQueue = { ...res.data.data };
          if (playQueue) {
            if (playQueue.items) {
              let duration = 0;
              for (let i = 0; i < playQueue.items.length; i++) {
                playQueue.items[i].position = i;
                playQueue.items[i].incoming = duration;
                duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              if (playQueue.items.length > 0) {
                set({ currentPerformance: playQueue.items[0] });
              }
              set({ playQueue: playQueue, playQueueDuration: duration });
            }
          }
        }
      })
      .catch(function () { });
  },
  getHistory: (sessionToken) => {
    MuvikaService.getMusicHistory(sessionToken)
      .then(function (res) {
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let history = { ...res.data.data };
          if (history) {
            if (history.items) {
              for (let i = 0; i < history.items.length; i++) {
                history.items[i].position = i;
              }
              set({ history: history });
            }
          }
        }
      })
      .catch(function () { });
  },
  setCurrentPlayList: (sessionToken, playList) => {
    MuvikaService.getMusicPlayList(sessionToken, playList)
      .then(function (res) {
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let playList = { ...res.data.data };
          let duration = 0;
          if (playList) {
            if (playList.items) {
              let dateNow = new Date();
              dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
              for (let i = 0; i < playList.items.length; i++) {
                playList.items[i].created = dateNow;
                playList.items[i].position = i;
                playList.items[i].incoming = duration;
                duration += playList.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              let playQueue = get().playQueue;
              if (playQueue) {
                if (playList.items.length <= maxPlayQueueSize) {
                  if (playList.items.length > 0) {
                    set({ currentPerformance: playList.items[0] });
                  }
                  playQueue.items = [...playList.items];
                  set({ playQueueDuration: duration, currentPlayList: playList });
                  MuvikaService.setMusicPlayList(sessionToken, playQueue)
                    .then(function (res) {
                      if (res.data.error) {
                        messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                      } else {
                        set({ playQueue: { ...playQueue } });
                      }
                    })
                    .catch(function () { });
                } else {
                  messageStore.getState().setError(sessionToken, 0, TR.cm_max_number_of_songs_reached);
                }
              }
            }
          }
        }
      })
      .catch(function () { });
  },
  addPlayListToTheEnd: (sessionToken, playList) => {
    MuvikaService.getMusicPlayList(sessionToken, playList)
      .then(function (res) {
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let playList = { ...res.data.data };
          if (playList) {
            if (playList.items) {
              let playQueue = get().playQueue;
              if (playQueue) {
                if (playQueue.items?.length + playList.items.length <= maxPlayQueueSize) {
                  let dateNow = new Date();
                  dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
                  for (let i = 0; i < playList.items.length; i++) {
                    playList.items[i].created = dateNow;
                    playList.items[i].unique_id = generateId(8);
                  }
                  playQueue.items.push(...playList.items);
                  let duration = 0;
                  for (let i = 0; i < playQueue.items.length; i++) {
                    playQueue.items[i].position = i;
                    playQueue.items[i].incoming = duration;
                    duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
                  }
                  if (playQueue.items.length > 0 && !get().currentPerformance) {
                    set({ currentPerformance: playQueue.items[0] });
                    if (get().nextPerformance) {
                      set({ nextPerformance: { ...playQueue.items[0] } });
                    }
                  }
                  set({ playQueueDuration: duration });
                  playQueue.items = [...playQueue.items];
                  set({ playQueueDuration: duration });
                  MuvikaService.setMusicPlayList(sessionToken, playQueue)
                    .then(function (res) {
                      if (res.data.error) {
                        messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                      } else {
                        set({ playQueue: { ...playQueue } });
                      }
                    })
                    .catch(function () { });
                } else {
                  messageStore.getState().setError(sessionToken, 0, TR.cm_max_number_of_songs_reached);
                }
              }
            }
          }
        }
      })
      .catch(function () { });
  },
  setPlayList: (sessionToken, playList) => {
    MuvikaService.setMusicPlayList(sessionToken, playList)
      .then(function (res) {
        console.log(playList);
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          set({ playQueue: { ...playList } });
        }
      })
      .catch(function () { });
  },
  clearHistory: (sessionToken) => {
    let history = get().history;
    history.items = [];
    get().setHistory(sessionToken, history);
  },
  setHistory: (sessionToken, history) => {
    MuvikaService.setMusicHistory(sessionToken, history)
      .then(function (res) {
        if (res.data.error) {
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          set({ history: { ...history } });
        }
      })
      .catch(function () { });
  },
  addPerformanceToHistory: (sessionToken, performance) => {
    let dateNow = new Date();
    dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
    let history = get().history;
    if (history) {
      if (history.items) {
        let newPerformance = {
          unique_id: generateId(8),
          created: dateNow,
          played: dateNow,
          pitch: performance.pitch,
          track: performance.track,
          profile: performance.profile
        };
        history.items.unshift(newPerformance);
        const settings = settingsStore.getState().settings;
        if (settings) {
          history.items = history.items.slice(0, Math.min(history.items.length, settings.history_size));
        }
        for (let i = 0; i < history.items.length; i++) {
          history.items[i].position = i;
        }
        get().setHistory(sessionToken, history);
      }
    }
  },
  prevFromHistory: (sessionToken) => {
    let history = get().history;
    let playQueue = get().playQueue;
    if (history && playQueue) {
      if (history.items) {
        if (history.items.length > 0) {
          let firstInHistory = history.items.shift();
          let dateNow = new Date();
          dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
          let performance = {
            unique_id: generateId(8),
            created: dateNow,
            played: null,
            pitch: firstInHistory.pitch,
            track: firstInHistory.track,
            profile: firstInHistory.profile,
          };
          playQueue.items.unshift(performance);
          let duration = 0;
          for (let i = 0; i < playQueue.items.length; i++) {
            playQueue.items[i].position = i;
            playQueue.items[i].incoming = duration;
            duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          set({ currentPerformance: { ...playQueue.items[0] } });
          if (get().nextPerformance) {
            set({ nextPerformance: { ...playQueue.items[0] } });
          }
          history.items = [...history.items];
          get().setPlayList(sessionToken, playQueue);
          get().setHistory(sessionToken, history);
        }
      }
    }
  },
  addPerformanceToPlayQueue: (sessionToken, track, singerProfile, toPos, isAutoAdded = false, playNow = false) => {
    let dateNow = new Date();
    dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
    if (!singerProfile) {
      console.log("singerProfile", track);
    }
    let performance = {
      unique_id: generateId(8),
      created: dateNow,
      played: null,
      pitch: 0,
      track: track,
      profile: singerProfile,
      flags: isAutoAdded ? "auto_added" : ""
    };
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        if (playQueue.items?.length < maxPlayQueueSize) {
          if (toPos || toPos == 0) {
            if (playQueue.items.length > toPos) {
              playQueue.items.splice(toPos, 0, performance);
            } else {
              playQueue.items.push(performance);
            }
          } else {
            playQueue.items.push(performance);
          }
          let duration = 0;
          for (let i = 0; i < playQueue.items.length; i++) {
            playQueue.items[i].position = i;
            playQueue.items[i].incoming = duration;
            duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          playQueue.items = [...playQueue.items];
          MuvikaService.setMusicPlayList(sessionToken, playQueue)
            .then(function (res) {
              if (res.data.error) {
                set({ isWaiting: false });
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                if (toPos == 0 && playNow) {
                  if (!musicPlayerStore.getState().isMusicOn) {
                    musicPlayerStore.getState().setIsMusicOn(true);
                    let newSettings = { ...settingsStore.getState().settings };
                    newSettings.music_enabled = true;
                    settingsStore.getState().setSettings(sessionToken, newSettings, false);
                  }
                  musicPlayerStore.getState().setIsPlaying(true, false);
                  musicPlayerStore.getState().setIsPaused(false, false);
                } else if (toPos == 0 && !playNow) {
                  musicPlayerStore.getState().setIsPlaying(false, false);
                  musicPlayerStore.getState().setIsPaused(true, false);
                }
                if (toPos == 0 || get().currentPerformance == null) {
                  set({ currentPerformance: playQueue.items[0] });
                }
                set({ playQueue: { ...playQueue } });
                set({ isWaiting: false });
              }
            })
            .catch(function () { });
        } else {
          messageStore.getState().setError(sessionToken, 0, TR.cm_max_number_of_songs_reached);
        }
      }
    }
  },
  addPerformancesToPlayQueue: (sessionToken, tracks, singerProfile, isAutoAdded = false) => {
    let dateNow = new Date();
    dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        if (playQueue.items?.length + tracks.length <= maxPlayQueueSize) {
          for (let i = 0; i < tracks.length; i++) {
            let performance = {
              unique_id: generateId(8),
              created: dateNow,
              played: null,
              pitch: 0,
              track: tracks[i],
              profile: singerProfile,
              flags: isAutoAdded ? "auto_added" : ""
            };
            playQueue.items.push(performance);
          }
          let duration = 0;
          for (let i = 0; i < playQueue.items.length; i++) {
            playQueue.items[i].position = i;
            playQueue.items[i].incoming = duration;
            duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          playQueue.items = [...playQueue.items];
          MuvikaService.setMusicPlayList(sessionToken, playQueue)
            .then(function (res) {
              if (res.data.error) {
                set({ isWaiting: false });
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                if (get().currentPerformance == null) {
                  set({ currentPerformance: playQueue.items[0] });
                }
                set({ playQueue: { ...playQueue } });
                set({ isWaiting: false });
              }
            })
            .catch(function () { });
        } else {
          messageStore.getState().setError(sessionToken, 0, TR.cm_max_number_of_songs_reached);
        }
      }
    }
  },
  editPerformanceInPlayList: (sessionToken, performanceId, singerProfile, playListId, pos = null) => {
    let playLists = get().playLists;
    let toPlayListId = playLists.findIndex(x => x.id == playListId);
    let toPlayList = playLists[toPlayListId];
    if (toPlayList) {
      if (toPlayList.items) {
        let performanceListId = toPlayList.items.findIndex(x => x.unique_id == performanceId);
        let performance = toPlayList.items[performanceListId];
        if (performance) {
          performance.profile = singerProfile;
          performance.pitch = 0;
          toPlayList.items[performanceListId] = performance;
          if (pos || pos == 0) {
            if (pos < toPlayList.items.length) {
              performance.position = pos;
              toPlayList.items.splice(performanceListId, 1);
              toPlayList.items.splice(pos, 0, { ...performance });
              let duration = 0;
              for (let i = 0; i < toPlayList.items.length; i++) {
                toPlayList.items[i].position = i;
                toPlayList.items[i].incoming = duration;
                duration += toPlayList.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
            }
          }
          MuvikaService.setMusicPlayList(sessionToken, toPlayList)
            .then(function (res) {
              if (res.data.error) {
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                messageStore.getState().setMessage(sessionToken, TR.cm_saved);
                set({ playLists: [...playLists] });
              }
            })
            .catch(function () { });
        }
      }
    }
  },
  addPerformanceToPlayList: (sessionToken, track, singerProfile, playListId, pos = null) => {
    let dateNow = new Date();
    dateNow.setMinutes(dateNow.getMinutes() + Math.round(accountStore.getState().timeDiff));
    let performance = {
      unique_id: generateId(8),
      created: dateNow,
      played: null,
      pitch: 0,
      track: track,
      profile: singerProfile
    };
    let toPlayListId = get().playLists.findIndex(x => x.id == playListId);
    let playLists = get().playLists;
    let toPlayList = playLists[toPlayListId];
    if (toPlayList) {
      if (toPlayList.items) {
        if (toPlayList.items.length < maxPlayQueueSize) {
          if (pos || pos == 0) {
            if (pos < toPlayList.items.length) {
              performance.position = pos;
              toPlayList.items.splice(pos, 0, { ...performance });
            } else {
              toPlayList.items.push({ ...performance });
            }
          } else {
            toPlayList.items.push({ ...performance });
          }
          let duration = 0;
          for (let i = 0; i < toPlayList.items.length; i++) {
            toPlayList.items[i].position = i;
            toPlayList.items[i].incoming = duration;
            duration += toPlayList.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          playLists[toPlayListId] = { ...toPlayList };
          MuvikaService.setMusicPlayList(sessionToken, toPlayList)
            .then(function (res) {
              if (res.data.error) {
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                messageStore.getState().setMessage(sessionToken, TR.cm_added);
                set({ playLists: [...playLists] });
              }
            })
            .catch(function () { });
        } else {
          messageStore.getState().setError(sessionToken, 0, TR.cm_max_number_of_songs_reached);
        }
      }
    }
  },
  getNextPerformance: () => {
    let playQueue = get().playQueue;
    if (playQueue) {
      let currentPosition = get().currentPosition;
      let nextPerformance = playQueue.items[currentPosition];
      if (nextPerformance) {
        set({ currentPerformance: nextPerformance });
        return nextPerformance;
      }
    }
  },
  setCurrentPosition: (pos, performance) => {
    set({ currentPosition: pos, currentPerformance: performance });
  },
  getPerformancePosition: (performanceId) => {
    let performancePosition = -1;
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        for (let i = 0; i < performances.length; i++) {
          let performance = performances[i];
          if (performance.unique_id === performanceId) {
            performancePosition = i;
            break;
          }
        }
      }
    }
    return performancePosition;
  },
  getPerformancePositionPlayList: (performanceId, playList) => {
    let performancePosition = -1;
    if (playList) {
      if (playList.items) {
        let performances = playList.items;
        for (let i = 0; i < performances.length; i++) {
          let performance = performances[i];
          console.log("search", performance, performanceId);
          if (performance.unique_id === performanceId) {
            performancePosition = i;
            break;
          }
        }
      }
    }
    return performancePosition;
  },
  swapPerformances: (sessionToken, performanceId1, performanceId2) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        //console.log(playQueue.items);
        let performancePosition1 = get().getPerformancePosition(performanceId1);
        let performancePosition2 = get().getPerformancePosition(performanceId2);
        if (performancePosition1 != -1 && performancePosition2 != -1) {
          var performance1 = { ...playQueue.items[performancePosition1] };
          var performance2 = { ...playQueue.items[performancePosition2] };
          performance1.position = performancePosition2;
          performance2.position = performancePosition1;
          playQueue.items[performancePosition1] = performance2;
          playQueue.items[performancePosition2] = performance1;
          if (playQueue.items.length > 0) {
            set({ currentPerformance: playQueue.items[0] });
            if (get().nextPerformance) {
              set({ nextPerformance: { ...playQueue.items[0] } });
            }
          }
          //console.log(playQueue.items);
          get().setPlayList(sessionToken, playQueue);
        }
      }
    }
  },
  movePerformanceInPlayList: (sessionToken, playListId, positionA, positionB) => {
    let playLists = get().playLists;
    let playListListId = playLists.findIndex(x => x.id == playListId);
    let playList = playLists[playListListId];
    if (playList) {
      if (playList.items) {
        let performances = playList.items;
        if (performances.length > positionA && performances.length > positionB) {
          let performanceA = { ...performances[positionA] };
          performances.splice(positionA, 1);
          performances.splice(positionB, 0, performanceA);
          let duration = 0;
          for (let i = 0; i < performances.length; i++) {
            performances[i].position = i;
            performances[i].incoming = duration;
            duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          playList.items = performances;
          MuvikaService.setMusicPlayList(sessionToken, playList)
            .then(function (res) {
              if (res.data.error) {
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                set({ playLists: [...playLists] });
              }
            })
            .catch(function () { });
        }
      }
    }
  },
  movePerformance: (sessionToken, positionA, positionB) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        if (performances.length > positionA && performances.length > positionB) {
          let performanceA = { ...performances[positionA] };
          performances.splice(positionA, 1);
          performances.splice(positionB, 0, performanceA);
          let duration = 0;
          for (let i = 0; i < performances.length; i++) {
            performances[i].position = i;
            performances[i].incoming = duration;
            duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          playQueue.items = performances;
          if (playQueue.items.length > 0) {
            set({ currentPerformance: playQueue.items[0] });
            if (get().nextPerformance) {
              set({ nextPerformance: { ...playQueue.items[0] } });
            }
          }
          get().setPlayList(sessionToken, playQueue);
        }
      }
    }
  },
  moveToSecond: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let performance = performances[performancePosition];
          if (performancePosition != 1) {
            const settings = settingsStore.getState().settings;
            performances.splice(performancePosition, 1);
            performances = [
              ...performances.slice(0, 1),
              performance,
              ...performances.slice(1)
            ];
            let duration = 0;
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            set({ playQueueDuration: duration });
            playQueue.items = performances;
            if (playQueue.items.length > 0) {
              set({ currentPerformance: playQueue.items[0] });
              if (get().nextPerformance) {
                set({ nextPerformance: { ...playQueue.items[0] } });
              }
            }
            get().setPlayList(sessionToken, playQueue);
          }
        }
      }
    }
  },
  moveToTop: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let performance = performances[performancePosition];
          if (performancePosition > 0) {
            const settings = settingsStore.getState().settings;
            performances.splice(performancePosition, 1);
            performances.unshift(performance);
            let duration = 0;
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            set({ playQueueDuration: duration });
            playQueue.items = performances;
            set({ currentPerformance: performance });
            if (get().nextPerformance) {
              set({ nextPerformance: { ...performance } });
            }
            get().setPlayList(sessionToken, playQueue);
          }
        }
      }
    }
  },
  moveToBottom: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let performance = performances[performancePosition];
          if (performancePosition >= 0) {
            performances.splice(performancePosition, 1);
            performances.push(performance);
            let duration = 0;
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            set({ playQueueDuration: duration });
            playQueue.items = performances;
            if (playQueue.items.length > 0) {
              set({ currentPerformance: playQueue.items[0] });
              if (get().nextPerformance) {
                set({ nextPerformance: { ...playQueue.items[0] } });
              }
            }
            get().setPlayList(sessionToken, playQueue);
          }
        }
      }
    }
  },
  moveUp: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let performance = performances[performancePosition];
          if (performancePosition > 0) {
            performances.splice(performancePosition, 1);
            performances.splice(performancePosition - 1, 0, performance);
            let duration = 0;
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            set({ playQueueDuration: duration });
            playQueue.items = performances;
            if (performancePosition - 1 == 0) {
              set({ currentPerformance: performance });
            }
            if (playQueue.items.length > 0) {
              set({ currentPerformance: playQueue.items[0] });
              if (get().nextPerformance) {
                set({ nextPerformance: { ...playQueue.items[0] } });
              }
            }
            get().setPlayList(sessionToken, playQueue);
          }
        }
      }
    }
  },
  moveDown: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let performance = performances[performancePosition];
          if (performancePosition < performances.length - 1) {
            performances.splice(performancePosition, 1);
            performances.splice(performancePosition + 1, 0, performance);
            let duration = 0;
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            set({ playQueueDuration: duration });
            playQueue.items = performances;
            if (playQueue.items.length > 0) {
              set({ currentPerformance: playQueue.items[0] });
              if (get().nextPerformance) {
                set({ nextPerformance: { ...playQueue.items[0] } });
              }
            }
            get().setPlayList(sessionToken, playQueue);
          }
        }
      }
    }
  },
  moveUpInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let performance = performances[performancePosition];
            if (performancePosition > 0) {
              performances.splice(performancePosition, 1);
              performances.splice(performancePosition - 1, 0, performance);
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              playList.items = performances;
              MuvikaService.setMusicPlayList(sessionToken, playList)
                .then(function (res) {
                  if (res.data.error) {
                    messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                  } else {
                    set({ playLists: [...playLists] });
                  }
                })
                .catch(function () { });
            }
          }
        }
      }
    }
  },
  moveDownInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let performance = performances[performancePosition];
            if (performancePosition < performances.length - 1) {
              performances.splice(performancePosition, 1);
              performances.splice(performancePosition + 1, 0, performance);
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              playList.items = performances;
              MuvikaService.setMusicPlayList(sessionToken, playList)
                .then(function (res) {
                  if (res.data.error) {
                    messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                  } else {
                    set({ playLists: [...playLists] });
                  }
                })
                .catch(function () { });
            }
          }
        }
      }
    }
  },
  moveToTopInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let performance = performances[performancePosition];
            if (performancePosition > 0) {
              performances.splice(performancePosition, 1);
              performances.unshift(performance);
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              playList.items = performances;
              MuvikaService.setMusicPlayList(sessionToken, playList)
                .then(function (res) {
                  if (res.data.error) {
                    messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                  } else {
                    set({ playLists: [...playLists] });
                  }
                })
                .catch(function () { });
            }
          }
        }
      }
    }
  },
  moveToSecondInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let performance = performances[performancePosition];
            if (performancePosition >= 0) {
              performances.splice(performancePosition, 1);
              performances.splice(1, 0, performance);
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              playList.items = performances;
              MuvikaService.setMusicPlayList(sessionToken, playList)
                .then(function (res) {
                  if (res.data.error) {
                    messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                  } else {
                    set({ playLists: [...playLists] });
                  }
                })
                .catch(function () { });
            }
          }
        }
      }
    }
  },
  moveToBottomInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let performance = performances[performancePosition];
            if (performancePosition >= 0) {
              performances.splice(performancePosition, 1);
              performances.push(performance);
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
              playList.items = performances;
              MuvikaService.setMusicPlayList(sessionToken, playList)
                .then(function (res) {
                  if (res.data.error) {
                    messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                  } else {
                    set({ playLists: [...playLists] });
                  }
                })
                .catch(function () { });
            }
          }
        }
      }
    }
  },
  clearPlayQueue: (sessionToken) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      playQueue.items = [];
      set({ playQueueDuration: 0, currentPerformance: null });
      get().setPlayList(sessionToken, playQueue);
      musicPlayerStore.getState().empty();
    }
  },
  isInFilter: (filter, track) => {
    console.log("filter", filter);
    console.log("filter", track);
    let isLang = false;
    if (Object.keys(filter.filterLanguages).length == 0) {
      isLang = true;
    } else if (filter.filterLanguages[track.language_id]) {
      isLang = filter.filterLanguages[track.language_id];
    }
    let isGenre = false;
    if (Object.keys(filter.filterGenres).length == 0) {
      isGenre = true;
    } else if (track.genre_ids) {
      track.genre_ids.forEach((genre) => {

        if (filter.filterGenres[genre]) {
          isGenre = filter.filterGenres[genre];
        }
      });
    }
    let isDecade = false;
    let decade = decades.find(x => {
      if (track.year >= x.start && track.year <= x.end) {
        return x;
      }
    });
    if (decade) {
      if (Object.keys(filter.filterDecades).length == 0) {
        isDecade = true;
      } else if (filter.filterDecades[decade.start]) {
        isDecade = filter.filterDecades[decade.start];
      }
    }
    console.log("filter", isLang, isDecade, isGenre);
    return isLang && isDecade && isGenre;
  },
  clearAllAutoAdded: (sessionToken) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = [];
        for (var i = 0; i < playQueue.items.length; i++) {
          let performance = playQueue.items[i];
          if (performance) {
            if (performance.flags) {
              if (!performance.flags.includes("auto_added")) {
                performances.push(performance);
              }
            } else {
              performances.push(performance);
            }
          }
        }
        playQueue.items = performances;
        let duration = 0;
        for (let i = 0; i < playQueue.items.length; i++) {
          playQueue.items[i].position = i;
          playQueue.items[i].incoming = duration;
          duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
        }
        set({ playQueueDuration: duration });
        MuvikaService.setMusicPlayList(sessionToken, playQueue)
          .then(function (res) {
            if (res.data.error) {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            } else {
              if (playQueue.items.length > 0) {
                set({ currentPerformance: playQueue.items[0] });
              } else {
                set({ currentPerformance: null });
              }
              set({ playQueue: { ...playQueue } });
            }
          })
          .catch(function () { });
      }
    }
  },
  filterOutPlayQueue: (sessionToken, filter, singer = null) => {
    console.log("filter out");
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = [];
        for (var i = 0; i < playQueue.items.length; i++) {
          let performance = playQueue.items[i];
          console.log("filter", performance);
          if (performance) {
            if (performance.flags) {
              console.log(performance.flags);
              if (performance.flags.includes("auto_added")) {
                if (get().isInFilter(filter, performance.track)) {
                  console.log("filter", "is in filter");
                  performances.push(performance);
                }
                continue;
              }
            }
            console.log("filter", "push");
            performances.push(performance);
          }
        }
        playQueue.items = performances;
        console.log("filter", playQueue);
        let duration = 0;
        for (let i = 0; i < playQueue.items.length; i++) {
          playQueue.items[i].position = i;
          playQueue.items[i].incoming = duration;
          duration += playQueue.items[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
        }
        set({ playQueueDuration: duration });
        MuvikaService.setMusicPlayList(sessionToken, playQueue)
          .then(function (res) {
            if (res.data.error) {
              messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
            } else {
              if (playQueue.items.length > 0) {
                set({ currentPerformance: playQueue.items[0] });
              } else {
                set({ currentPerformance: null });
              }
              set({ playQueue: { ...playQueue } });
              if (playQueue.items.length < 4) {
                get().getMoreTracks(sessionToken, 5, singer, filter);
              }
            }
          })
          .catch(function () { });
      }
    }
  },
  getMoreTracks: (sessionToken, pageSize, profile, filter) => {
    console.log("getMoreTracks()");
    set({ isWaiting: true });
    let orderBy = "random";
    let langFilters = filter.filterLanguages;
    let genreFilters = filter.filterGenres;
    let decadeFilters = filter.filterDecades;
    let items = MuvikaService.createMusicParameters(
      langFilters,
      genreFilters,
      decadeFilters
    );
    MuvikaService.getTracksForMusic(sessionToken, pageSize, 1, items, orderBy)
      .then(function (res) {
        console.log(res);
        if (res.data.error) {
          set({ isSet: false, tracks: [], totalCount: 0, isWaiting: false });
          messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
        } else {
          let latestReqId = get().latestReqId;
          let newReqId = res.data.request_id;
          if (newReqId > latestReqId) {
            let playQueue = get().playQueue;
            console.log(playQueue);
            if (playQueue) {
              let tracks = res.data.data.items;
              let localProfile = { ...profile };
              let addingTracks = [];
              tracks.forEach((track) => {

                console.log("adding", track);
                if (!localProfile) {
                  console.log("singerProfile", track);
                }
                addingTracks.push(track);
                //get().addPerformanceToPlayQueue(sessionToken, track, localProfile, null, true);
              });
              get().addPerformancesToPlayQueue(sessionToken, addingTracks, localProfile, true);
            } else {
              set({ isWaiting: false });
            }
            set({ latestReqId: newReqId });
          }
        }
      })
      .catch(function () { });
  },
  deletePerformance: (sessionToken, performanceId) => {
    let playQueue = get().playQueue;
    console.log("deletePerformance", playQueue);
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performancePosition = get().getPerformancePosition(performanceId);
        if (performancePosition != -1) {
          let duration = 0;
          performances.splice(performancePosition, 1);
          for (let i = 0; i < performances.length; i++) {
            performances[i].position = i;
            performances[i].incoming = duration;
            duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
          }
          set({ playQueueDuration: duration });
          playQueue.items = performances;
          if (playQueue.items.length == 0) {
            set({ currentPerformance: null });
          } else {
            set({ currentPerformance: { ...playQueue.items[0] } });
            if (get().nextPerformance) {
              set({ nextPerformance: { ...playQueue.items[0] } });
            }
          }
          console.log("deletePerformance", playQueue);
          get().setPlayList(sessionToken, playQueue);
        }
      }
    }
  },
  deletePerformanceInPlayList: (sessionToken, playListId, performanceId) => {
    let playLists = get().playLists;
    if (playLists) {
      let playListIndex = playLists.findIndex(x => x.id == playListId);
      if (playListIndex != -1) {
        let playList = playLists[playListIndex];
        if (playList.items) {
          let performances = playList.items;
          let performancePosition = get().getPerformancePositionPlayList(performanceId, playList);
          if (performancePosition != -1) {
            let duration = 0;
            performances.splice(performancePosition, 1);
            for (let i = 0; i < performances.length; i++) {
              performances[i].position = i;
              performances[i].incoming = duration;
              duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
            }
            playList.items = performances;
            MuvikaService.setMusicPlayList(sessionToken, playList)
              .then(function (res) {
                if (res.data.error) {
                  messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
                } else {
                  set({ playLists: [...playLists] });
                }
              })
              .catch(function () { });
          }
        }
      }
    }
  },
  deletePerformanceFromHistory: (sessionToken, performanceId) => {
    let history = get().history;
    if (history) {
      if (history.items) {
        let performances = history.items;
        console.log("search", performanceId);
        let performancePosition = get().getPerformancePositionPlayList(performanceId, history);
        console.log(performancePosition);
        if (performancePosition != -1) {
          performances.splice(performancePosition, 1);
          for (let i = 0; i < performances.length; i++) {
            performances[i].position = i;
          }
          history.items = performances;
          get().setHistory(sessionToken, history);
        }
      }
    }
  },
  editPerformance: (sessionToken, performanceId, singer, pos = null, playNow = false) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performances = playQueue.items;
        let performanceIndex = performances.findIndex(x => x.unique_id == performanceId);
        let performance = performances[performanceIndex];
        if (performance) {
          performance.unique_id = generateId(8);
          performance.profile = singer;
          performances[performanceIndex] = performance;
          let oldPos = performance.position;
          if (pos || pos == 0) {
            if (pos < performances.length) {
              performance.position = pos;
              performances.splice(performanceIndex, 1);
              performances.splice(pos, 0, { ...performance });
              let duration = 0;
              for (let i = 0; i < performances.length; i++) {
                performances[i].position = i;
                performances[i].incoming = duration;
                duration += performances[i].track.length.split(':').reduce((acc, time) => (60 * acc) + +time);
              }
            }
          }
          playQueue.items = performances;
          console.log(performances);
          MuvikaService.setMusicPlayList(sessionToken, playQueue)
            .then(function (res) {
              if (res.data.error) {
                messageStore.getState().setError(sessionToken, res.data.error.code, res.data.error.details.length > 0 ? res.data.error.details : res.data.error.string);
              } else {
                if ((oldPos !== performance.position) && (oldPos == 0 || performance.position == 0)) {
                  set({ currentPerformance: { ...performances[0] } });
                }
                if (performance.position == 0 && playNow) {
                  musicPlayerStore.getState().setIsPlaying(true, false);
                  musicPlayerStore.getState().setIsPaused(false, false);
                }
                if ((oldPos !== performance.position) && (performance.position == 0 || get().currentPerformance == null)) {
                  set({ currentPerformance: playQueue.items[0] });
                }
                set({ playQueue: { ...playQueue } });
                set({ isWaiting: false });
              }
            })
            .catch(function () { });
        }
      }
    }
  },
  editPerformancePlayList: (sessionToken, playListId, performanceId, singer, pitch, track = null) => {
    let playLists = get().playLists;
    let playListIndex = playLists.findIndex(x => x.id == playListId);
    let playList = playLists[playListIndex];
    if (playList) {
      if (playList.items) {
        let performances = playList.items;
        let performanceIndex = performances.findIndex(x => x.unique_id == performanceId);
        let performance = performances[performanceIndex];
        if (performance) {
          performance.profile = singer;
          performance.pitch = pitch;
          if (track) {
            performance.track = track;
          }
          performances[performanceIndex] = performance;
          playList.items = performances;
          get().setPlayList(sessionToken, playList);
          playLists[playListIndex] = playList;
          set({ playList: [...playLists] });
        }
      }
    }
  },
  setPerformanceToBeChanged: (performance) => {
    set({ performanceToBeChanged: performance });
  },
  changeTrackPerformance: (sessionToken, track, singerProfile) => {
    let playQueue = get().playQueue;
    if (playQueue) {
      if (playQueue.items) {
        let performanceToBeChanged = get().performanceToBeChanged;
        if (performanceToBeChanged) {
          performanceToBeChanged.unique_id = generateId(8);
          performanceToBeChanged.track = track;
          performanceToBeChanged.pitch = 0;
          performanceToBeChanged.profile = singerProfile;
          playQueue.items[performanceToBeChanged.position] = performanceToBeChanged;
          if (performanceToBeChanged.position == 0) {
            set({ currentPerformance: { ...performanceToBeChanged } });
          }
          set({ performanceToBeChanged: null });
          get().setPlayList(sessionToken, playQueue);
        }
      }
    }
  }
})));

export default bgPerformanceStore;